import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Check } from 'lucide-react';
import type { LearningPath, NodeContent } from '@shared/types/learning-path';
import { NodeContentRenderer } from './NodeContentRenderer';
import { ProgressTracker } from './ProgressTracker';

interface CardGridLayoutProps {
  path: LearningPath;
  nodeContents: Map<string, NodeContent>;
  currentNodeId: string | null;
  onNavigate: (nodeId: string) => void;
  onComplete: (nodeId: string) => void;
}

const nodeTypeIcons: Record<string, string> = {
  learn: '📖',
  checkpoint: '✓',
  exercise: '⚡',
  reflection: '💭',
  video: '▶',
  synthesis: '🔗',
};

export function CardGridLayout({ path, nodeContents, currentNodeId, onNavigate, onComplete }: CardGridLayoutProps) {
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const completedSet = new Set(path.progress.completedNodes);

  function handleCardClick(nodeId: string) {
    setExpandedNodeId(nodeId);
    onNavigate(nodeId);
  }

  function handleClose() {
    if (expandedNodeId) onComplete(expandedNodeId);
    setExpandedNodeId(null);
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Progress bar */}
      <ProgressTracker path={path} currentNodeId={currentNodeId} onNavigate={handleCardClick} variant="bar" />

      {/* Card grid */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {path.nodes.map((node, i) => {
            const content = nodeContents.get(node.nodeId);
            const isCompleted = completedSet.has(node.nodeId);
            const isCheckpoint = node.nodeType === 'checkpoint';

            return (
              <motion.button
                key={node.nodeId}
                onClick={() => handleCardClick(node.nodeId)}
                className={`text-left p-5 rounded-lg border transition-colors cursor-pointer
                  ${isCompleted ? 'bg-dark-surface border-l-[3px] border-l-indigo-500 border-dark-border' : ''}
                  ${isCheckpoint && !isCompleted ? 'bg-dark-surface border-gold-500/30 border' : ''}
                  ${!isCompleted && !isCheckpoint ? 'bg-dark-surface border-dark-border hover:bg-dark-hover hover:border-dark-border-light' : ''}
                `}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                whileHover={{ y: -2 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-lg">{nodeTypeIcons[node.nodeType] ?? '📄'}</span>
                  {isCompleted && <Check size={14} className="text-success" />}
                </div>
                <p className="font-body text-sm text-content font-medium mb-2">
                  {content?.content?.title ?? node.conceptIds.join(', ')}
                </p>
                <div className="flex items-center gap-2 text-xs text-content-dim font-body">
                  <Clock size={12} />
                  <span>{node.estimatedDuration} min</span>
                  <span className="capitalize">{node.contentFormat}</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Expanded card overlay */}
      <AnimatePresence>
        {expandedNodeId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-dark-bg/90 backdrop-blur-sm overflow-y-auto"
          >
            <div className="max-w-3xl mx-auto px-4 py-8">
              <button
                onClick={handleClose}
                className="mb-6 flex items-center gap-2 text-sm text-content-muted hover:text-content transition-colors cursor-pointer"
              >
                <X size={16} /> Close
              </button>

              {(() => {
                const node = path.nodes.find((n) => n.nodeId === expandedNodeId);
                const content = nodeContents.get(expandedNodeId);
                if (!node || !content) return <p className="text-content-muted font-body">Loading...</p>;

                return (
                  <NodeContentRenderer
                    nodeContent={content}
                    node={node}
                    pathId={path.pathId}
                  />
                );
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
